import { useState, useEffect, useCallback } from "react";
import { Search, Filter, RefreshCw, X, FileSpreadsheet, Download, Factory, Package, Calendar, User, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { downloadExcel } from "@/utils/excelExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionPlanningReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateField, setDateField] = useState("requested_delivery_date");
  const [sortField, setSortField] = useState("date"); // "date", "sales_order", "quantity", "value"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc", "desc"
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  const fetchReport = useCallback(async () => {
    setError(null);
    try {
      const response = await axios.get(`${API}/reports/production-planning`);
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
      setError(error.response?.data?.detail || "Failed to load production planning report");
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    setLoading(true);
    fetchReport();
    toast.success("Report refreshed");
  };
  
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setDateField("requested_delivery_date");
    setSortField("date");
    setSortOrder("asc");
  };

  const setDatePreset = (preset) => {
    const today = new Date();
    const formatDateStr = (d) => d.toISOString().split("T")[0];

    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "this-month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(formatDateStr(firstDay));
      setEndDate(formatDateStr(lastDay));
    } else if (preset === "next-30") {
      const next30 = new Date(today);
      next30.setDate(today.getDate() + 30);
      setStartDate(formatDateStr(today));
      setEndDate(formatDateStr(next30));
    } else if (preset === "next-90") {
      const next90 = new Date(today);
      next90.setDate(today.getDate() + 90);
      setStartDate(formatDateStr(today));
      setEndDate(formatDateStr(next90));
    }
  };

  const handleSortToggle = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  
  // Filter & Sort data
  const filteredData = reportData
    .filter(item => {
      const matchesSearch = !searchQuery || 
        item.sales_order.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.material.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.material_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.production_order && item.production_order.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesStatus = statusFilter === "all" || 
        item.sales_order_status.toLowerCase().includes(statusFilter.toLowerCase());

      let matchesDate = true;
      const itemDateStr = item[dateField];
      if (startDate || endDate) {
        if (!itemDateStr) {
          matchesDate = false;
        } else {
          const formattedItemDate = itemDateStr.split("T")[0];
          if (startDate && formattedItemDate < startDate) matchesDate = false;
          if (endDate && formattedItemDate > endDate) matchesDate = false;
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortField === "date") {
        const valA = a[dateField] ? new Date(a[dateField]).getTime() : 0;
        const valB = b[dateField] ? new Date(b[dateField]).getTime() : 0;
        comparison = valA - valB;
      } else if (sortField === "sales_order") {
        comparison = (a.sales_order || "").localeCompare(b.sales_order || "");
      } else if (sortField === "quantity") {
        comparison = (a.order_quantity || 0) - (b.order_quantity || 0);
      } else if (sortField === "value") {
        comparison = (a.net_value || 0) - (b.net_value || 0);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  
  const hasActiveFilters = searchQuery || statusFilter !== "all" || startDate || endDate || sortField !== "date" || sortOrder !== "asc";
  
  // Calculate stats
  const stats = {
    totalOrders: [...new Set(filteredData.map(d => d.sales_order))].length,
    totalItems: filteredData.length,
    withPO: filteredData.filter(d => d.production_order).length,
    withoutPO: filteredData.filter(d => !d.production_order).length,
    totalValue: filteredData.reduce((sum, d) => sum + d.net_value, 0)
  };
  
  const formatCurrency = (value, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === "INR" ? "INR" : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Open</Badge>;
      case "partial delivery":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Partial</Badge>;
      case "completed":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status || "Unknown"}</Badge>;
    }
  };
  
  const getProductionStatusBadge = (status) => {
    if (!status) return <span className="text-muted-foreground text-sm">No PO</span>;
    
    switch (status?.toLowerCase()) {
      case "in progress":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>;
      case "completed":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };
  
  const exportToCSV = () => {
    const headers = [
      "Sales Order", "Item", "Customer", "Material", "Description", 
      "Quantity", "Unit", "Requested Delivery Date", "Committed Date", "Production Order", 
      "PO Quantity", "PO Status", "PO Planned End", "SO Status", "Value"
    ];
    
    const rows = filteredData.map(item => [
      item.sales_order,
      item.sales_order_item,
      item.customer_name,
      item.material,
      item.material_description,
      item.order_quantity,
      item.unit,
      item.requested_delivery_date || "",
      item.committed_date || "",
      item.production_order || "",
      item.production_quantity || "",
      item.production_status || "",
      item.production_planned_end || "",
      item.sales_order_status,
      item.net_value
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `production_planning_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported to CSV");
  };
  
  return (
    <div data-testid="production-planning-report">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title heading-display" data-testid="page-title">Production Planning Report</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Sales Orders with Production Order status from SAP
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToCSV} 
            variant="outline" 
            className="btn-industrial"
            disabled={filteredData.length === 0}
            data-testid="export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => downloadExcel("/export/planning-report", "planning_report.xlsx")} 
            variant="outline" 
            className="btn-industrial"
            disabled={filteredData.length === 0}
            data-testid="export-excel-button"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            className="btn-industrial"
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="stat-card" data-testid="stat-orders">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="stat-card-value">{stats.totalOrders}</p>
              <p className="stat-card-label">Sales Orders</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-items">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-500/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="stat-card-value">{stats.totalItems}</p>
              <p className="stat-card-label">Line Items</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-with-po">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
              <Factory className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="stat-card-value">{stats.withPO}</p>
              <p className="stat-card-label">With Prod Order</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-without-po">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center">
              <Factory className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="stat-card-value">{stats.withoutPO}</p>
              <p className="stat-card-label">Without Prod Order</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-value">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-purple-500/10 flex items-center justify-center">
              <span className="text-purple-600 font-bold text-lg">₹</span>
            </div>
            <div>
              <p className="stat-card-value text-lg">{formatCurrency(stats.totalValue, "INR")}</p>
              <p className="stat-card-label">Total Value</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filter & Date Control Bar */}
      <div className="space-y-3 mb-6">
        <div className="filter-bar flex-wrap gap-3 p-3 bg-card border border-border/60 rounded-xl shadow-xs" data-testid="filter-bar">
          {/* Search Input */}
          <div className="search-input-wrapper flex-1 min-w-[220px]">
            <Search />
            <Input
              placeholder="Search SO, customer, material, PO..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="search-input"
            />
          </div>
          
          {/* SO Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="filter-status">
              <Filter className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder="SO Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="partial">Partial Delivery</SelectItem>
            </SelectContent>
          </Select>

          {/* Date Type Target */}
          <Select value={dateField} onValueChange={setDateField}>
            <SelectTrigger className="w-[175px] h-9">
              <Calendar className="w-3.5 h-3.5 mr-2 text-blue-600" />
              <SelectValue placeholder="Date Target" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="requested_delivery_date">Req. Delivery Date</SelectItem>
              <SelectItem value="committed_date">Committed Date</SelectItem>
            </SelectContent>
          </Select>

          {/* Start Date */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium">From:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[140px] h-9 text-xs"
              data-testid="start-date-input"
            />
          </div>

          {/* End Date */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium">To:</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[140px] h-9 text-xs"
              data-testid="end-date-input"
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearFilters}
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}

          {/* Results Badge */}
          <Badge variant="secondary" className="ml-auto font-mono text-xs">
            {filteredData.length} items
          </Badge>
        </div>

        {/* Quick Date Presets Bar */}
        <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <span className="font-semibold text-slate-600 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-blue-500" />
            Quick Presets:
          </span>
          <button
            onClick={() => setDatePreset("all")}
            className={`px-2.5 py-1 rounded-md transition-colors ${!startDate && !endDate ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
          >
            All Dates
          </button>
          <button
            onClick={() => setDatePreset("this-month")}
            className={`px-2.5 py-1 rounded-md transition-colors ${startDate && endDate ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
          >
            This Month
          </button>
          <button
            onClick={() => setDatePreset("next-30")}
            className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
          >
            Next 30 Days
          </button>
          <button
            onClick={() => setDatePreset("next-90")}
            className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
          >
            Next 90 Days
          </button>
        </div>
      </div>
      
      {/* Report Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>
      ) : error ? (
        <div className="empty-state" data-testid="error-state">
          <FileSpreadsheet className="empty-state-icon text-destructive" />
          <h3 className="text-lg font-medium mb-2">Failed to Load Report</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" onClick={handleRefresh}>
            Try Again
          </Button>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          <FileSpreadsheet className="empty-state-icon" />
          <h3 className="text-lg font-medium mb-2">No Data Found</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters 
              ? "Try adjusting your filters or date range"
              : "No open sales orders found (completed orders older than 1 week are excluded)"
            }
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <Card className="card-industrial" data-testid="report-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-semibold cursor-pointer select-none hover:text-blue-600 transition-colors" onClick={() => handleSortToggle("sales_order")}>
                    <div className="flex items-center gap-1">
                      Sales Order
                      <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Customer
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">Material</TableHead>
                  <TableHead className="font-semibold text-right cursor-pointer select-none hover:text-blue-600 transition-colors" onClick={() => handleSortToggle("quantity")}>
                    <div className="flex items-center justify-end gap-1">
                      Qty
                      <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none hover:text-blue-600 transition-colors" onClick={() => { setDateField("requested_delivery_date"); handleSortToggle("date"); }}>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      Req. Delivery
                      {sortField === "date" && dateField === "requested_delivery_date" && (
                        sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" /> : <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none hover:text-blue-600 transition-colors" onClick={() => { setDateField("committed_date"); handleSortToggle("date"); }}>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                      Committed
                      {sortField === "date" && dateField === "committed_date" && (
                        sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-emerald-600 font-bold" /> : <ArrowDown className="w-3.5 h-3.5 text-emerald-600 font-bold" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1">
                      <Factory className="w-4 h-4" />
                      Production Order
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-right">PO Qty</TableHead>
                  <TableHead className="font-semibold">PO Status</TableHead>
                  <TableHead className="font-semibold">SO Status</TableHead>
                  <TableHead className="font-semibold text-right cursor-pointer select-none hover:text-blue-600 transition-colors" onClick={() => handleSortToggle("value")}>
                    <div className="flex items-center justify-end gap-1">
                      Value
                      <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item, idx) => (
                  <TableRow 
                    key={`${item.sales_order}-${item.sales_order_item}-${idx}`}
                    className="hover:bg-secondary/30 transition-colors"
                    data-testid={`report-row-${item.sales_order}-${item.sales_order_item}`}
                  >
                    <TableCell>
                      <div>
                        <span className="data-value font-medium">{item.sales_order}</span>
                        <span className="text-muted-foreground text-xs ml-1">/ {item.sales_order_item}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{item.customer_name}</p>
                        <p className="text-xs text-muted-foreground data-value">{item.customer_number}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <p className="text-sm line-clamp-1">{item.material_description || item.material}</p>
                              <p className="text-xs text-muted-foreground data-value">{item.material}</p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{item.material_description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="data-value font-medium">{item.order_quantity.toLocaleString()}</span>
                      <span className="text-muted-foreground text-xs ml-1">{item.unit}</span>
                    </TableCell>
                    <TableCell>
                      <span className="data-value" data-testid={`req-delivery-${item.sales_order}-${item.sales_order_item}`}>
                        {formatDate(item.requested_delivery_date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`data-value ${item.committed_date ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`} data-testid={`committed-date-${item.sales_order}-${item.sales_order_item}`}>
                        {formatDate(item.committed_date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {item.production_order ? (
                        <div>
                          <span className="data-value font-medium text-emerald-600">{item.production_order}</span>
                          {item.production_planned_end && (
                            <p className="text-xs text-muted-foreground">
                              Plan: {formatDate(item.production_planned_end)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.production_quantity ? (
                        <span className="data-value">{item.production_quantity.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getProductionStatusBadge(item.production_status)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(item.sales_order_status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="data-value">{formatCurrency(item.net_value, item.currency)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductionPlanningReport;

